/**
 * ml-image-zoom.js
 * Opens the current product main image in a BiggerPicture lightbox at width=2048.
 * Covers two renderer types used across all ~21 product templates:
 *   Adapter A — ai_gen blocks (default, giftcard, bulkcustomer templates)
 *   Adapter B — stock main-product section (mlbulkproduct*, bundle, company, etc.)
 *
 * BiggerPicture (bigger-picture.min.js) must be loaded before this script.
 */
(function () {
  // Guard against double-init (e.g. Shopify section re-renders)
  if (window.__mlImageZoom) return;
  window.__mlImageZoom = true;

  /**
   * Upgrade a Shopify image URL to width=2048.
   * Handles three cases: ?width=N already present, &width=N in a longer QS, or absent entirely.
   */
  function hires(u) {
    if (!u) return u;
    if (/[?&]width=\d+/.test(u)) {
      return u.replace(/([?&])width=\d+/, '$1width=2048');
    }
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=2048';
  }

  /** Aspect ratio (height / width) of a loaded <img>, or a fallback. */
  function ratioOf(imgEl, fallback) {
    if (imgEl && imgEl.naturalWidth && imgEl.naturalHeight) {
      return imgEl.naturalHeight / imgEl.naturalWidth;
    }
    return fallback || 1;
  }

  /**
   * Build a BiggerPicture item. Supplying width/height (correct aspect) prevents
   * the image being stretched into a wrong-aspect box, and thumb avoids a bogus
   * "undefined" thumbnail request.
   */
  function makeItem(fullUrl, thumbEl, fallbackRatio) {
    var ratio = ratioOf(thumbEl, fallbackRatio);
    var W = 2048;
    var H = Math.round(W * ratio) || W;
    var item = { img: hires(fullUrl), width: W, height: H };
    if (thumbEl) {
      var thumb = thumbEl.currentSrc || thumbEl.src;
      if (thumb) item.thumb = thumb;
    }
    return item;
  }

  // ── Adapter A: ai_gen_block_1c829e5 (standard) + ai_gen_block_b4fb5fb ──────
  // (giftcard, bulkcustomer). Both share the ai-product-info__* class prefix.

  function getAdapterAMainImage() {
    return document.querySelector('[id^="main-image-"]');
  }

  /**
   * Build items from thumbnail buttons' data-full (in DOM order) plus the index
   * of the active thumbnail. Falls back to the single main image if there are
   * no thumbnails. Recomputed at click time so variant changes are respected.
   */
  function getAdapterAState() {
    var mainImg = getAdapterAMainImage();
    if (!mainImg) return null;

    var mainRatio = ratioOf(mainImg, 1);
    var thumbBtns = Array.from(
      document.querySelectorAll('[class*="ai-product-info__thumbnail-"]')
    );

    var items = [];
    var currentIndex = 0;
    thumbBtns.forEach(function (btn) {
      var full = btn.getAttribute('data-full');
      if (!full) return;
      if (btn.classList.contains('active')) currentIndex = items.length;
      items.push(makeItem(full, btn.querySelector('img'), mainRatio));
    });

    // No thumbnails → single main image
    if (!items.length) {
      items = [makeItem(mainImg.currentSrc || mainImg.src, mainImg, mainRatio)];
      currentIndex = 0;
    }

    return { items: items, currentIndex: currentIndex };
  }

  // ── Adapter B: stock main-product gallery (bulk/bundle/company/etc.) ───────

  /** All stock-gallery media images, in DOM order. */
  function getAdapterBImages() {
    return Array.from(
      document.querySelectorAll(
        '.product__media-list .product__media-item .product__media img'
      )
    );
  }

  /**
   * Build items from every media image, plus the index of the clicked image.
   * Recomputed at click time so variant/slide changes are respected.
   */
  function getAdapterBStateFor(clickedImg) {
    var imgs = getAdapterBImages();
    if (!imgs.length && clickedImg) imgs = [clickedImg];

    var fallbackRatio = ratioOf(clickedImg, 1);
    var currentIndex = 0;
    var items = imgs.map(function (img, i) {
      if (img === clickedImg) currentIndex = i;
      return makeItem(img.currentSrc || img.src, img, fallbackRatio);
    });

    return { items: items, currentIndex: currentIndex };
  }

  // ── Make a main image element a keyboard/click zoom trigger ───────────────

  function attachZoomTrigger(mainImg, getState) {
    // Accessibility: announce as an interactive zoom control
    mainImg.setAttribute('role', 'button');
    mainImg.setAttribute('tabindex', '0');
    mainImg.setAttribute('aria-label', 'Zoom image');

    function openZoom(e) {
      e.preventDefault();
      if (typeof BiggerPicture === 'undefined') return; // library missing → no-op

      // Re-read DOM state at click time so variant/slide changes are respected
      var state = getState();
      if (!state || !state.items.length) return;

      var bp = BiggerPicture({ target: document.body });
      bp.open({ items: state.items, position: state.currentIndex });
    }

    mainImg.addEventListener('click', openZoom);
    mainImg.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') openZoom(e);
    });
  }

  /** Stop the stock native modal-opener link so only our zoom fires (Adapter B). */
  function suppressNativeOpener(mainImg) {
    var openerLink = mainImg.closest('a.product__modal-opener, a[data-modal-open]');
    if (openerLink) {
      openerLink.addEventListener('click', function (e) { e.preventDefault(); });
    }
  }

  // ── Initialise whichever adapter is present ───────────────────────────────

  function init() {
    // Adapter A (ai_gen block) is the active renderer when present — use it
    // exclusively so we never double-bind a hidden stock gallery on the same page.
    var adapterAImg = getAdapterAMainImage();
    if (adapterAImg) {
      attachZoomTrigger(adapterAImg, getAdapterAState);
      return;
    }

    // Adapter B: stock main-product gallery (bulk/bundle/company/etc.).
    // On desktop this renders as a column of several images, so bind EACH
    // media image; each opens the lightbox at its own position.
    var bImgs = getAdapterBImages();
    bImgs.forEach(function (img) {
      suppressNativeOpener(img);
      attachZoomTrigger(img, function () { return getAdapterBStateFor(img); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already parsed (script loaded with defer — the normal path)
    init();
  }
})();
